/**
 * TaskStatusBadge Component
 *
 * Displays a badge for task status with consistent styling.
 * Follows the color scheme defined in the SPEC.
 *
 * @module components/tasks/TaskStatusBadge
 */

import { memo } from 'react';
import {
  Inbox,
  Circle,
  Play,
  Eye,
  FlaskConical,
  CheckCircle,
  Ban,
  Bot,
  UserCheck
} from 'lucide-react';

// ============================================
// Constants
// ============================================

/**
 * Task status configuration
 * Colors and icons for each status
 */
export const TASK_STATUS_CONFIG = {
  backlog: {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    label: 'Backlog',
    Icon: Inbox
  },
  todo: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    label: 'To Do',
    Icon: Circle
  },
  doing: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/30',
    label: 'In Progress',
    Icon: Play
  },
  agent_test: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    label: 'Agent Test',
    Icon: Bot
  },
  user_test: {
    bg: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    border: 'border-cyan-500/30',
    label: 'User Test',
    Icon: UserCheck
  },
  review: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    label: 'Review',
    Icon: Eye
  },
  testing: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    label: 'Testing',
    Icon: FlaskConical
  },
  done: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    label: 'Done',
    Icon: CheckCircle
  },
  blocked: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
    label: 'Blocked',
    Icon: Ban
  }
};

/**
 * Size variants for the badge
 */
const SIZE_CLASSES = {
  sm: {
    badge: 'px-1.5 py-0.5 text-xs',
    icon: 'w-3 h-3'
  },
  md: {
    badge: 'px-2 py-1 text-xs',
    icon: 'w-3.5 h-3.5'
  },
  lg: {
    badge: 'px-3 py-1.5 text-sm',
    icon: 'w-4 h-4'
  }
};

// ============================================
// Component
// ============================================

/**
 * TaskStatusBadge Component
 *
 * @param {object} props - Component props
 * @param {string} props.status - Task status value
 * @param {string} [props.size='md'] - Badge size: 'sm', 'md', or 'lg'
 * @param {boolean} [props.showIcon=true] - Whether to show the status icon
 * @param {boolean} [props.showLabel=true] - Whether to show the status label
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Badge element
 */
const TaskStatusBadge = memo(function TaskStatusBadge({
  status,
  size = 'md',
  showIcon = true,
  showLabel = true,
  className = ''
}) {
  const config = TASK_STATUS_CONFIG[status];

  if (!config) {
    console.warn(`Unknown task status: ${status}`);
    return null;
  }

  const { bg, text, border, label, Icon } = config;
  const sizeConfig = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium
        border ${bg} ${text} ${border}
        ${sizeConfig.badge}
        ${className}
      `}
    >
      {showIcon && <Icon className={sizeConfig.icon} />}
      {showLabel && <span>{label}</span>}
    </span>
  );
});

export default TaskStatusBadge;
